import { useTranslation } from 'react-i18next';
import type { CargoArticleWire, LogisticsCargoCandidate } from '@magimp/shared';
import { useNavigate } from 'react-router';

import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import {
  Button,
  EditorModal,
  RowCard,
  ToolbarActions,
  type RowCardField,
} from '../../../ui/index.js';
import { canSeeCargoCarrierPaymentInCard } from '../role.js';
import { cargoColumnHeaderKey, visibleCargoColumns, type CargoColumn } from './cargoColumns.js';

export interface CargoReadOnlyModalProps {
  row: CargoArticleWire;
  onClose: () => void;
}

type CargoDetailColumn = CargoColumn & {
  key: Exclude<CargoColumn['key'], 'index' | 'delete'>;
};

function isCargoDetailColumn(column: CargoColumn): column is CargoDetailColumn {
  return column.key !== 'index' && column.key !== 'delete';
}

export function CargoReadOnlyModal({ row, onClose }: CargoReadOnlyModalProps) {
  const { t, i18n } = useTranslation(['cargo']);
  const user = useAuthStore((state) => state.user);
  const permissions = useAuthStore((state) => state.permissions);
  const navigate = useNavigate();
  const canCreateProblem =
    hasPermission(permissions, 'cargoProblems.read') &&
    hasPermission(permissions, 'cargoProblems.create');
  const canCreateTtn =
    hasPermission(permissions, 'logisticsCalculations.read') &&
    hasPermission(permissions, 'logisticsCalculations.manage');
  const columns = visibleCargoColumns(canSeeCargoCarrierPaymentInCard(user)).filter(
    isCargoDetailColumn,
  );
  const context = { t, locale: i18n.language };
  const fields: RowCardField[] = columns.map((column) => ({
    label: t(cargoColumnHeaderKey(column.key)),
    value: column.render(row, context, 1),
  }));

  return (
    <EditorModal
      open
      title={t('cargo:forms.editTitle', { article: row.article })}
      width={560}
      dirty={false}
      onConfirmClose={onClose}
    >
      <div data-testid="cargo-readonly-card">
        {canCreateProblem || canCreateTtn ? (
          <ToolbarActions style={{ justifyContent: 'flex-end', marginBottom: 'var(--s-4)' }}>
            {canCreateTtn ? (
              <Button
                kind="secondary"
                onClick={() => {
                  onClose();
                  navigate('/ttn/new', {
                    state: {
                      logisticsCalculationDraftCandidate: cargoToTtnCandidate(row),
                    },
                  });
                }}
              >
                {t('cargo:actions.createTtn')}
              </Button>
            ) : null}
            {canCreateProblem ? (
              <Button
                kind="primary"
                icon="alert-triangle"
                onClick={() => {
                  onClose();
                  navigate('/cargo/problems/new', {
                    state: {
                      cargoProblemDraftCargo: {
                        id: row.id,
                        article: row.article,
                        nameGoods: row.nameGoods,
                        transportationTariff: row.transportationTariff,
                        weight: row.weight,
                        volume: row.volume,
                        carrier: row.carrier,
                      },
                    },
                  });
                }}
              >
                {t('cargo:actions.reportProblem')}
              </Button>
            ) : null}
          </ToolbarActions>
        ) : null}
        <RowCard ariaLabel={row.article} fields={fields} />
      </div>
    </EditorModal>
  );
}

function cargoToTtnCandidate(row: CargoArticleWire): LogisticsCargoCandidate {
  return {
    id: row.id,
    article: row.article,
    name: row.nameGoods,
    seats: row.numberOfSeats,
    weight: row.weight,
    volume: row.volume,
    transportType: row.pathFormat,
    departedAt: row.timeFromChina,
    arrivedAt: row.timeCargoArrivalToRf,
    expenseSnapshot: {
      ...(row.chinaPaymentAmount !== null ? { chinaPayment: row.chinaPaymentAmount } : {}),
      ...(row.chinaWarehouseDispatchAmount !== null
        ? { chinaWarehouseDispatch: row.chinaWarehouseDispatchAmount }
        : {}),
      ...(row.chinaWarehouseInspectionAmount !== null
        ? { chinaWarehouseInspection: row.chinaWarehouseInspectionAmount }
        : {}),
      ...(row.chinaWarehousePackagingAmount !== null
        ? { chinaWarehousePackaging: row.chinaWarehousePackagingAmount }
        : {}),
      ...(row.transportationTariff !== null
        ? { transportationTariff: row.transportationTariff }
        : {}),
      ...(row.transportationTariffForClients !== null
        ? { transportationTariffForClients: row.transportationTariffForClients }
        : {}),
      ...(row.insuranceCost !== null ? { insuranceCost: row.insuranceCost } : {}),
      ...(row.packagingCost !== null ? { packagingCost: row.packagingCost } : {}),
      ...(row.addressTransportationCost !== null
        ? { addressTransportationCost: row.addressTransportationCost }
        : {}),
      ...(row.prr !== null ? { prr: row.prr } : {}),
      ...(row.tatCost !== null ? { tatCost: row.tatCost } : {}),
    },
    suggestedInternalCostLines: [],
  };
}
